/**
 * The `/licenses` page's data — the public client bundle's own third-party
 * notices (#8914).
 *
 * The file is DERIVED in the bundle action from the bundle's own metafile
 * (`@letsgethome/build-tools/third-party-notices`) and held equal to a fresh
 * derivation by `//web/apps/public:third_party_notices_check`. The page FETCHES
 * it — never imports it: an import would put the notices inside the very
 * bundle whose metafile defines them. Its name is the ONE constant the writer
 * and the gate use too, so the three cannot disagree.
 */
import { THIRD_PARTY_NOTICES_ASSET } from '@letsgethome/build-tools/third-party-notices-asset';

/** The served URL: the SSR server serves `dist/client/<f>` at `/assets/<f>`. */
export const THIRD_PARTY_NOTICES_URL = `/assets/${THIRD_PARTY_NOTICES_ASSET}`;

/** Where a row's code reaches the bundle from. */
export type NoticeSource = 'npm' | 'crate' | 'toolchain' | 'vendored' | 'embedded';

/** One shipped component. */
export interface NoticeRow {
  readonly source: NoticeSource;
  readonly name: string;
  readonly version: string;
  /** An SPDX expression. */
  readonly licence: string;
  /** Keys into {@link ThirdPartyNotices.texts}. */
  readonly texts: readonly string[];
  /** `#<issue>` while no honest text exists (a dated override row). */
  readonly pending?: string;
  /** The wasm / generated input / host package the row ships in. */
  readonly via?: readonly string[];
  /** The package's author, when its text is the SPDX canonical one. */
  readonly author?: string;
  /** An embedded component's own copyright line, quoted from its host's dist. */
  readonly copyright?: string;
}

/** The whole file. */
export interface ThirdPartyNotices {
  readonly schema: number;
  readonly app: string;
  readonly packages: readonly NoticeRow[];
  readonly texts: Readonly<Record<string, string>>;
}

/** The page's three sections, in render order. */
export type NoticeGroup = 'packages' | 'wasm' | 'embedded';

const GROUP_OF: Record<NoticeSource, NoticeGroup> = {
  npm: 'packages',
  crate: 'wasm',
  toolchain: 'wasm',
  vendored: 'wasm',
  embedded: 'embedded',
};

/** Rows by section, each section sorted by name. Empty sections are dropped. */
export function groupNotices(
  notices: ThirdPartyNotices
): Array<{ group: NoticeGroup; rows: NoticeRow[] }> {
  const order: NoticeGroup[] = ['packages', 'wasm', 'embedded'];
  return order
    .map((group) => ({
      group,
      rows: notices.packages
        .filter((r) => GROUP_OF[r.source] === group)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name) || a.version.localeCompare(b.version)),
    }))
    .filter((g) => g.rows.length > 0);
}

/** A file this page can render: the shape the writer emits, or nothing. */
export function parseNotices(value: unknown): ThirdPartyNotices | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<ThirdPartyNotices>;
  if (v.schema !== 1 || !Array.isArray(v.packages) || !v.texts || typeof v.texts !== 'object') {
    return null;
  }
  return v as ThirdPartyNotices;
}
